import React, { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import MainPageWrapper from "@/components/MainPageWrapper/MainPageWrapper";
import DataTable from "@/components/Datatable/Datatable";
import { useChapterActivities } from "@/features/myChapterActivity/hooks/useChapterActivities";
import { createChapterActivitiesColumns } from "@/features/myChapterActivity/components/ChapterActivitiesColumns/ChapterActivitiesColumns";
import ChapterActivitiesFilters from "@/features/myChapterActivity/components/ChapterActivitiesFilters/ChapterActivitiesFilters";
import ChapterActivityDetails from "@/features/myChapterActivity/components/ChapterActivityDetails/ChapterActivityDetails";
import { ChapterActivity } from "@/api/fetchChapterActivities/types";

function ChapterActivitiesList() {
  const navigate = useNavigate();
  const {
    activities,
    totalCount,
    loading,
    error,
    pagination,
    sorting,
    filters,
    handlePaginationChange,
    handleSortingChange,
    handleFilterChange,
  } = useChapterActivities();

  // Details modal state
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [activityToView, setActivityToView] = useState<ChapterActivity | null>(
    null,
  );

  // View details handler
  const handleViewDetails = useCallback((activity: ChapterActivity) => {
    setActivityToView(activity);
    setDetailsModalOpen(true);
  }, []);

  const handleCloseDetails = useCallback(() => {
    setDetailsModalOpen(false);
    setActivityToView(null);
  }, []);

  // Navigate to create new activity form
  const handleCreateNew = useCallback(() => {
    navigate("/submit-activity");
  }, [navigate]);

  // Build columns with our callbacks
  const columns = useMemo(
    () =>
      createChapterActivitiesColumns({
        onViewDetails: handleViewDetails,
      }),
    [handleViewDetails],
  );

  // Determine message based on filter
  const getNoDataMessage = () => {
    if (filters.filter === "my") {
      return "You haven't submitted any chapter activities yet.";
    }
    return "No chapter activities found for your chapter.";
  };

  return (
    <MainPageWrapper error={error} title="Chapter Activities">
      <div className="mb-6">
        <p className="text-gray-600">
          {filters.filter === "my"
            ? "View all activities you've submitted for your chapter."
            : "View all activities from your chapter members."}
        </p>
      </div>

      <ChapterActivitiesFilters
        filters={filters}
        onCreateNew={handleCreateNew}
        onFilterChange={handleFilterChange}
      />

      <DataTable
        columns={columns}
        data={activities}
        error={error}
        loading={loading}
        noDataMessage={getNoDataMessage()}
        onPageChange={(pageIndex, pageSize) =>
          handlePaginationChange({
            pageIndex,
            pageSize,
          })
        }
        onSortingChange={handleSortingChange}
        pageIndex={pagination.pageIndex}
        pageSize={pagination.pageSize}
        rowKeyExtractor={(item) => String(item.id)}
        sorting={sorting}
        totalCount={totalCount}
      />

      {/* Details Modal */}
      <ChapterActivityDetails
        activity={activityToView}
        isOpen={detailsModalOpen}
        onClose={handleCloseDetails}
      />
    </MainPageWrapper>
  );
}

export default ChapterActivitiesList;
